/**
 * Classifies a failed `/auth/v1/token?grant_type=refresh_token` response.
 *
 * "Invalid Refresh Token: Already Used" is BENIGN when it is a race — two
 * parallel refreshes, the other one won and the session already holds the new
 * token. It is TERMINAL when it repeats: the stored refresh token is dead
 * (rotated away by another client sharing the cookie, or its family revoked)
 * and every further attempt will 400 forever. The old code classified the
 * message as benign unconditionally, so a dead token produced a tight loop —
 * ~190 identical 400s in ~35 s, no backoff, no "session expired" surface, and
 * the Conference silently degraded to a signed-out view (extreme module 08
 * defect 14, staging 2026-08-17). After ALREADY_USED_BENIGN_MAX occurrences
 * inside ALREADY_USED_WINDOW_MS the same message counts as terminal, which
 * lets the existing circuit breaker clear the dead auth state and raise the
 * ReauthModal.
 */
export const ALREADY_USED_BENIGN_MAX = 3;
export const ALREADY_USED_WINDOW_MS = 30_000;

export type RefreshFailureKind = 'benign_race' | 'terminal' | 'other';

export interface AlreadyUsedTracker {
  count: number;
  windowStart: number;
}

export function createAlreadyUsedTracker(): AlreadyUsedTracker {
  return { count: 0, windowStart: 0 };
}

export function isAlreadyUsedMessage(message: string): boolean {
  const m = message.toLowerCase();
  return m.includes('refresh token') && m.includes('already used');
}

/**
 * Records one "already used" observation and says whether it is still within
 * the benign-race allowance. Mutates the tracker.
 */
export function classifyAlreadyUsed(tracker: AlreadyUsedTracker, now: number): RefreshFailureKind {
  if (now - tracker.windowStart > ALREADY_USED_WINDOW_MS) {
    tracker.count = 0;
    tracker.windowStart = now;
  }
  tracker.count += 1;
  return tracker.count <= ALREADY_USED_BENIGN_MAX ? 'benign_race' : 'terminal';
}

/**
 * Why the SERVER ended a session, read from a failed refresh (A70).
 *  - 'idle': GoTrue's inactivity timeout (GOTRUE_SESSIONS_INACTIVITY_TIMEOUT) refused the refresh:
 *    code `session_expired`, message "Invalid Refresh Token: Session Expired (Inactivity)".
 *  - 'session_expired': the session is gone for another reason (timebox, revoked, deleted): code
 *    `session_expired` without "inactivity", `session_not_found`, `refresh_token_not_found`.
 * No retry and no re-auth-by-refresh can bring either back. Anything else (network, 5xx, "Already Used")
 * is null and keeps its existing handling.
 */
export type ServerEndedSessionReason = 'idle' | 'session_expired';

const SESSION_GONE_CODES = new Set(['session_expired', 'session_not_found', 'refresh_token_not_found']);
const SESSION_GONE_MESSAGES = [
  'session expired',
  'session not found',
  'refresh token not found',
  'session from session_id claim in jwt does not exist',
];

function collectCodesAndMessages(err: unknown, depth = 0): { codes: string[]; messages: string[] } {
  const out = { codes: [] as string[], messages: [] as string[] };
  if (err === null || err === undefined || depth > 2) return out;
  if (typeof err === 'string') {
    out.messages.push(err);
    return out;
  }
  if (typeof err !== 'object') return out;
  const o = err as Record<string, unknown>;
  for (const k of ['code', 'error_code']) {
    if (typeof o[k] === 'string') out.codes.push((o[k] as string).toLowerCase());
  }
  for (const k of ['message', 'msg', 'error_description']) {
    if (typeof o[k] === 'string') out.messages.push(o[k] as string);
  }
  // Circuit-breaker / wrapper details: { error }, { cause }, { reason } may carry the underlying error.
  for (const k of ['error', 'cause', 'reason']) {
    const nested = collectCodesAndMessages(o[k], depth + 1);
    out.codes.push(...nested.codes);
    out.messages.push(...nested.messages);
  }
  return out;
}

export function classifyServerEndedSession(err: unknown): ServerEndedSessionReason | null {
  const { codes, messages } = collectCodesAndMessages(err);
  const text = messages.join('\n').toLowerCase();
  if (isAlreadyUsedMessage(text)) return null;
  const gone =
    codes.some((c) => SESSION_GONE_CODES.has(c)) || SESSION_GONE_MESSAGES.some((m) => text.includes(m));
  if (!gone) return null;
  return text.includes('inactivity') ? 'idle' : 'session_expired';
}

/**
 * The code and message of a failed GoTrue auth response body (A70). GoTrue v2 answers a refused refresh with
 * `{ code: 400, error_code: 'session_expired', msg: 'Invalid Refresh Token: Session Expired (Inactivity)' }`;
 * older and OAuth-style bodies use `error` / `error_description` / `message`. A body that is not an object
 * yields empty strings.
 */
export interface AuthErrorSummary {
  code: string;
  message: string;
}

export function summarizeAuthErrorBody(data: unknown): AuthErrorSummary {
  if (!data || typeof data !== 'object') return { code: '', message: '' };
  const o = data as Record<string, unknown>;
  const str = (v: unknown) => (typeof v === 'string' ? v : '');
  return {
    code: str(o.error_code) || str(o.code),
    message: str(o.error_description) || str(o.error) || str(o.message) || str(o.msg),
  };
}

/**
 * The `auth:circuit-breaker` event detail the browser client dispatches. It CARRIES the underlying error so the
 * consumer (GlobalReauthHandler -> classifyServerEndedSession) can tell an inactivity sign-out from any other
 * terminal failure; a bare `{ reason }` made the idle notice unreachable.
 */
export function circuitBreakerDetail(
  reason: 'auth_error_loop' | 'session_idle',
  error: AuthErrorSummary,
): { reason: string; error: AuthErrorSummary } {
  return { reason, error: { code: error.code, message: error.message } };
}
